import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { createAd, updateAd, deleteAd } from "../redux/actions/adsActions";
import { clearAdState } from "../redux/reducers/adsSlice";
import Toast from "../common/toast-message/Toast";
import useToast from "../common/toast-message/useToast";

const Ads = () => {
  const { toastMessage, showToastMessage, hideToast } = useToast();
  const dispatch = useDispatch();
  const { adminData } = useSelector((state) => state.admin);
  const {
    loading: adLoading,
    error: adError,
    success: adSuccess,
    updateLoading: updateAdLoading,
    updateError: updateAdError,
    updateSuccess: updateAdSuccess,
    deleteLoading: deleteAdLoading,
    deleteError: deleteAdError,
    deleteSuccess: deleteAdSuccess,
  } = useSelector((state) => state.ads);

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [deletingAdId, setDeletingAdId] = useState(null);
  const [adData, setAdData] = useState({
    title: "",
    fromDate: "",
    toDate: "",
    redirectionLink: "",
    thumbnail: "",
    type: "5",
    enableLikes: false,
    enableShares: false,
    adStatus: "active",
  });
  const [imageFile, setImageFile] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteAdPending, setDeleteAdPending] = useState(null);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const totalEntries = ads.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentAds = ads.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const isThumbnailRequired = (type) => {
    return ["2", "5"].includes(type);
  };

  const extractAdId = (ad) => {
    if (ad.adId) return ad.adId;
    if (ad.id) return ad.id;
    if (ad.PK && ad.PK.startsWith("AD#")) {
      return ad.PK.replace("AD#", "");
    }
    return ad.PK || "unknown-id";
  };

  const generateImageName = (adId, title) => {
    const cleanTitle = title
      ? title
          .replace(/[^a-zA-Z0-9]/g, "-")
          .toLowerCase()
          .substring(0, 30)
      : "ad";
    return `${cleanTitle}-${adId.substring(0, 8)}`;
  };

  const getTypeDisplayName = (type) => {
    const typeMap = {
      2: "Secondary Banner Ad",
      5: "Primary Banner Ad",
    };
    return typeMap[type] || `Type ${type}`;
  };

  const getTypeDescription = (type) => {
    const descriptionMap = {
      2: "Secondary banner display",
      5: "Primary banner display",
    };
    return descriptionMap[type] || "Banner Ad";
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${process.env.REACT_APP_API_BASE_USER_URL}/isGuest/ads-list`;
      console.log("Fetching Ads from:", url);

      const response = await axios.get(url);
      console.log("Ads API Response:", response.data);

      const activeAds = response.data.filter((ad) => ad.adStatus === "active");
      setAds(activeAds);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching ads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adSuccess && !adLoading) {
      showToastMessage('Ad Created Successfully!');
      setShowCreatePopup(false);
      setEditingAd(null);
      resetForm();
      fetchAds();
      
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (updateAdSuccess && !updateAdLoading) {
      showToastMessage('Ad Updated Successfully!');
      setShowCreatePopup(false);
      setEditingAd(null);
      resetForm();
      fetchAds();
      
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (deleteAdSuccess && !deleteAdLoading) {
      showToastMessage('Ad Deleted Successfully!');
      setShowDeletePopup(false);
      setDeleteAdPending(null);
      fetchAds();
      
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (adError && !adLoading) {
      showToastMessage('Failed to create ad: ' + adError);
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (updateAdError && !updateAdLoading) {
      showToastMessage('Failed to update ad: ' + updateAdError);
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (deleteAdError && !deleteAdLoading) {
      showToastMessage('Failed to delete ad: ' + deleteAdError);
      const timer = setTimeout(() => {
        dispatch(clearAdState());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    adSuccess, adLoading, adError,
    updateAdSuccess, updateAdLoading, updateAdError,
    deleteAdSuccess, deleteAdLoading, deleteAdError,
    showToastMessage, dispatch
  ]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file (PNG, JPG, JPEG)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }

    try {
      const base64Data = await fileToBase64(file);
      setAdData({ ...adData, thumbnail: base64Data });
      setImageFile(file);
    } catch (error) {
      alert("Error processing image: " + error.message);
    }
  };

  const handleOpenCreatePopup = () => {
    dispatch(clearAdState());
    setShowCreatePopup(true);
    setEditingAd(null);
    resetForm();
  };

  const handleOpenEditPopup = (ad) => {
    dispatch(clearAdState());
    setEditingAd(ad);
    setAdData({
      title: ad.title || "",
      fromDate: ad.fromDate || "",
      toDate: ad.toDate || "",
      redirectionLink: ad.redirectionLink || "",
      thumbnail: "",
      type: ad.type || "5",
      enableLikes: ad.enableLikes || false,
      enableShares: ad.enableShares || false,
      adStatus: ad.adStatus || "active",
    });
    setImageFile(null);
    setShowCreatePopup(true);
  };

  const handleCloseCreatePopup = () => {
    dispatch(clearAdState());
    setShowCreatePopup(false);
    setEditingAd(null);
    resetForm();
  };

  const resetForm = () => {
    setAdData({
      title: "",
      fromDate: "",
      toDate: "",
      redirectionLink: "",
      thumbnail: "",
      type: "5",
      enableLikes: false,
      enableShares: false,
      adStatus: "active",
    });
    setImageFile(null);
  };

  const handleCreateAd = () => {
    if (!adminData) {
      alert("Admin data not available");
      return;
    }

    const thumbnailRequired = isThumbnailRequired(adData.type);

    if (!adData.title.trim()) {
      alert("Please fill in Ad Title");
      return;
    }

    if (thumbnailRequired && !adData.thumbnail) {
      alert("Please upload a Thumbnail Image for this ad type");
      return;
    }

    if (!adData.fromDate) {
      alert("Please select From Date");
      return;
    }

    const payload = {
      id: adminData.id,
      title: adData.title,
      fromDate: adData.fromDate,
      toDate: "",
      enableLikes: adData.enableLikes,
      enableShares: adData.enableShares,
      redirectionLink: adData.redirectionLink || "",
      adStatus: adData.adStatus,
      thumbnail: adData.thumbnail,
      type: adData.type,
    };

    console.log("Creating Ad with payload:", {
      ...payload,
      thumbnail: adData.thumbnail
        ? `Base64 image data (${adData.thumbnail.length} chars)`
        : "No thumbnail",
      adType: getTypeDisplayName(adData.type),
    });

    dispatch(createAd(adminData, payload));
  };

  const handleUpdateAd = () => {
    if (!adminData || !editingAd) {
      alert("Admin data or ad data not available");
      return;
    }

    const adId = extractAdId(editingAd);
    const thumbnailRequired = isThumbnailRequired(adData.type);

    if (!adData.title.trim()) {
      alert("Please fill in Ad Title");
      return;
    }

    if (thumbnailRequired && !adData.thumbnail && !editingAd.thumbnail) {
      alert("Please upload a Thumbnail Image for this ad type");
      return;
    }

    if (!adData.fromDate) {
      alert("Please select From Date");
      return;
    }

    const payload = {
      id: adminData.id,
      adId: adId,
      title: adData.title,
      fromDate: adData.fromDate,
      toDate: editingAd.toDate || "",
      enableLikes: adData.enableLikes,
      enableShares: adData.enableShares,
      redirectionLink: adData.redirectionLink || "",
      adStatus: adData.adStatus,
      type: adData.type,
      thumbnail: adData.thumbnail || "",
      existingThumbnailUrl: editingAd.thumbnail,
    };

    console.log("Updating Ad with payload:", {
      ...payload,
      thumbnail: adData.thumbnail
        ? `New base64 image data`
        : "No new thumbnail",
      existingThumbnailUrl: editingAd.thumbnail
        ? "Has existing thumbnail"
        : "No existing thumbnail",
      adType: getTypeDisplayName(adData.type),
    });
    
    dispatch(updateAd(adminData, payload));
  };

  const handleDeleteAd = async (ad) => {
    const adId = extractAdId(ad);
    setDeletingAdId(adId);

    try {
      await dispatch(deleteAd(adminData, adId));
    } catch (error) {
      showToastMessage("Failed to delete Ad: " + error.message);
    } finally {
      setDeletingAdId(null);
    }
  };

  const openDeleteConfirmation = (ad) => {
    setDeleteAdPending(ad);
    setShowDeletePopup(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      if (dateString.includes("-")) {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const truncateUrl = (url, maxLength = 30) => {
    if (!url) return "No Link";
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  const isFormValid = () => {
    if (!adData.title.trim()) return false;
    if (!adData.fromDate) return false;
    
    const thumbnailRequired = isThumbnailRequired(adData.type);
    if (thumbnailRequired && !adData.thumbnail && !editingAd?.thumbnail)
      return false;

    return true;
  };

  const isLoading = adLoading || updateAdLoading;
  const currentAdId = deletingAdId;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <svg
          className="animate-spin h-12 w-12 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-96 flex-col">
        <div className="text-red-500 text-lg mb-4">Error: {error}</div>
        <button
          onClick={fetchAds}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Toast message={toastMessage} onClose={hideToast} />
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          <h1 className="text-xl font-bold text-gray-900">Ads Management</h1>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 text-sm"
            onClick={handleOpenCreatePopup}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Ad
          </button>
        </div>

        <div className="flex items-center justify-between py-2 flex-wrap gap-2">
          <div className="text-xs sm:text-sm text-gray-600">
            Showing {totalEntries === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(endIndex, totalEntries)} of {totalEntries} entries
          </div>
          <div className="flex items-center gap-1">
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                  currentPage === page
                    ? "bg-green-400 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                }`}
              >
                {page}
              </button>
            ))}
            {totalPages > 1 && (
              <button
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ›
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {ads.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="w-12 h-12 text-gray-400 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-base font-medium text-gray-900 mb-2">
                No Ads Found
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                Get started by creating your first ad campaign.
              </p>
              <button
                onClick={handleOpenCreatePopup}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
              >
                Create Your First Ad
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Ad Details
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Type
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Start Date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Link
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ads.map((ad, index) => (
                    <tr
                      key={ad.PK || index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {ad.thumbnail ? (
                              <img
                                src={`${ad.thumbnail}?t=${ad.updatedAt || ad.createdAt || Date.now()}`}
                                alt={ad.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <span
                              className={`text-white font-semibold text-xs ${
                                ad.thumbnail ? "hidden" : "flex"
                              } items-center justify-center w-full h-full`}
                            >
                              {getTypeDisplayName(ad.type).charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                              {ad.title || "No Title"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(ad.createdAt)}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              ad.type === "5"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {getTypeDisplayName(ad.type)}
                          </span>
                          <span className="text-xs text-gray-500">
                            With Thumbnail
                          </span>
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <span className="text-xs">
                              {formatDate(ad.fromDate)}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {ad.redirectionLink ? (
                          <a
                            href={ad.redirectionLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 truncate max-w-[120px] text-xs"
                            title={ad.redirectionLink}
                          >
                            <svg
                              className="w-3 h-3 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            {truncateUrl(ad.redirectionLink, 20)}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-xs">No link</span>
                        )}
                      </td>

                      <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-bold
      ${
        ad.adStatus === "active"
          ? "bg-[#11c964] text-white"
          : "bg-[#f32121] text-white"
      }`}
                          style={{ minWidth: 60, textAlign: "center" }}
                        >
                          {ad.adStatus === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleOpenEditPopup(ad)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-600 hover:text-gray-800"
                            title="Edit Ad"
                            disabled={
                              deleteAdLoading && currentAdId === extractAdId(ad)
                            }
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button 
                            onClick={() => openDeleteConfirmation(ad)}
                            disabled={deleteAdLoading && currentAdId === extractAdId(ad)}
                            className="p-1 hover:bg-red-50 rounded transition-colors text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Ad"
                          >
                            {deleteAdLoading && currentAdId === extractAdId(ad) ? (
                              <svg className="animate-spin h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Ad Popup - UPDATED TO MATCH RJ.JS STYLE (NO PREVIEW, NO REMOVE BUTTON) */}
        {showCreatePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-800">
                  {editingAd ? "Edit Ad" : "Create New Ad"}
                </h2>
                <button
                  onClick={handleCloseCreatePopup}
                  className="text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                >
                  ✕
                </button>
              </div>

              <div className="p-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Title *
                    </label>
                    <input
                      type="text"
                      value={adData.title}
                      onChange={(e) =>
                        setAdData({ ...adData, title: e.target.value })
                      }
                      placeholder="Enter ad title"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Type
                    </label>
                    <select
                      value={adData.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setAdData({ ...adData, type: newType });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    >
                      <option value="5">Primary Banner Ad</option>
                      <option value="2">Secondary Banner Ad</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {getTypeDescription(adData.type)} • Thumbnail required
                    </p>
                  </div>

                  {/* UPDATED: SIMPLE IMAGE UPLOAD WITHOUT PREVIEW OR REMOVE BUTTON (LIKE RJ.JS) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Thumbnail Image {editingAd ? "(Optional)" : "*"}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG, JPEG (Max 5MB). Image will be converted to base64 and stored in S3.
                    </p>
                    {editingAd?.thumbnail && !imageFile && (
                      <p className="text-xs text-gray-500 mt-1">
                        Current thumbnail exists. Upload new image to replace it.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={adData.fromDate}
                      onChange={(e) =>
                        setAdData({ ...adData, fromDate: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={adData.adStatus}
                      onChange={(e) => setAdData({ ...adData, adStatus: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Redirection Link
                    </label>
                    <input
                      type="url"
                      value={adData.redirectionLink}
                      onChange={(e) =>
                        setAdData({
                          ...adData,
                          redirectionLink: e.target.value,
                        })
                      }
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={adData.enableLikes}
                        onChange={(e) =>
                          setAdData({
                            ...adData,
                            enableLikes: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Enable Likes
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={adData.enableShares}
                        onChange={(e) =>
                          setAdData({
                            ...adData,
                            enableShares: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Enable Shares
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCloseCreatePopup}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingAd ? handleUpdateAd : handleCreateAd}
                    disabled={isLoading || !isFormValid()}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {editingAd ? "Updating..." : "Creating..."}
                      </span>
                    ) : editingAd ? (
                      "Update Ad"
                    ) : (
                      "Create Ad"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {showDeletePopup && deleteAdPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm mx-4">
            <h4 className="mb-2 text-lg font-semibold text-gray-900">Delete Ad</h4>
            <p className="mb-3 text-gray-700">
              Are you sure you want to delete ad&nbsp;
              <span className="font-semibold">{deleteAdPending.title}</span>?
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                className="px-4 py-2 bg-gray-100 rounded-full text-gray-700 hover:bg-gray-200"
                onClick={() => { setShowDeletePopup(false); setDeleteAdPending(null); }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-60"
                onClick={async () => {
                  await handleDeleteAd(deleteAdPending);
                  setShowDeletePopup(false);
                  setDeleteAdPending(null);
                }}
                disabled={deleteAdLoading}
              >
                {deleteAdLoading && deletingAdId === extractAdId(deleteAdPending) 
                   ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toastMessage} onClose={hideToast} />
    </div>
  );
};

export default Ads;